import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../lib/sanity-client";
import renderModule from "../../../lib/render-module";
import { toSuperHeroProps } from "../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { OurStory } from "../../../lib/sanity-schema";
import { BaseProps, filters } from "../../../lib/sanity/shared";

interface PageData extends BaseProps {
  ourStory: OurStory;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const ourStory = await sanityClient().fetch<OurStory>(`*[
    ${filters.whereType("ourStory")} &&
    ${filters.excludeDrafts}
  ][0]`);

  return {
    props: {
      ourStory,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: OurStory) {
  if (!data.title) {
    throw Error("Our Story page must have a title.");
  }
}

const OurStoryPage: FunctionComponent<PageData> = ({
  ourStory,
  ...layoutWrapperProps
}) => {
  validate(ourStory);
  const { region, lang } = layoutWrapperProps;

  return (
    <LayoutWrapper
      {...{
        ...layoutWrapperProps,
        pageTitle: ourStory.title,
        metaTags: ourStory.metaTags,
      }}
    >
      {ourStory.hero?.length > 0 && (
        <SuperHero {...toSuperHeroProps(ourStory.hero, region, lang)} />
      )}
      {ourStory.modules?.length > 0 &&
        ourStory.modules.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default OurStoryPage;
